import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface InvoiceRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceId: string;
  planName: string;
}

export default function InvoiceRequestDialog({
  open,
  onOpenChange,
  priceId,
  planName,
}: InvoiceRequestDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [billingEmail, setBillingEmail] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    const body: Record<string, string> = { priceId };
    if (billingEmail.trim()) body.billingEmail = billingEmail.trim();
    if (poNumber.trim()) body.poNumber = poNumber.trim();

    const { data, error } = await supabase.functions.invoke('stripe-invoice', { body });

    setLoading(false);

    if (error || !data?.success) {
      toast({
        title: 'Something went wrong',
        description: error?.message ?? 'Unable to send invoice. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    onOpenChange(false);
    toast({
      title: 'Invoice sent!',
      description: 'Your account is now active. Check your email for the invoice.',
    });
    navigate('/dashboard?upgrade=success');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request an invoice — {planName}</DialogTitle>
          <DialogDescription>
            We'll email you an invoice. Your account will be active immediately.
            WriteAssess is not currently VAT registered, so VAT will not be applied to your invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="billingEmail">
              Billing email <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="billingEmail"
              type="email"
              placeholder="e.g. finance@school.gov.uk"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use your account email. Enter a different address to send the invoice to your finance office.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="poNumber">
              Purchase order number <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="poNumber"
              placeholder="e.g. PO-2024-0042"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Don't have one yet? Leave blank — you can provide it after receiving the invoice.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
